import React, { useState } from 'react';
import * as S from './styles';
import { IconDocumentSignature, Checkbox, Button } from '@veneer/core';
import { customAddMonths, formatDate } from 'src/utils/dateUtil';
import { useTranslationWithInterpolation } from 'src/hooks/useTranslationWithValues/useTranslationWithValuesHelper';
import {
  CancellationFeeItem,
  PrinterItem
} from 'src/utils/Products/productFilter';
import { formatPrice } from 'src/utils/Products/getCalculatePriceDifferance';
import { useLazyPlaceOrder } from 'src/services/MagentoClient';
import RequestErrorModal from '../../RequestErrorModal';
import { PlaceOrderResponse, UpgradeCart } from 'src/types/graphql/product';
import { internalURLs } from 'src/components/config/internal-link';
import useModifiedUrl from 'src/hooks/useModifiedUrl';

interface TermsofServiceComponentProps {
  selectedPrinterDetails: PrinterItem;
  onBack: () => void;
  onConfirm?: () => void;
  cartInfo?: UpgradeCart;
  setOrderNumber?: (orderNumber: string) => void;
}

const TermsofServiceComponent = ({
  selectedPrinterDetails,
  onBack,
  onConfirm,
  cartInfo,
  setOrderNumber
}: TermsofServiceComponentProps): JSX.Element => {
  const { getText, getTextWithValues } = useTranslationWithInterpolation();

  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [understandChecked, setUnderstandChecked] = useState(false);
  const [orderCreationError, setOrderCreationError] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [placeOrder] = useLazyPlaceOrder();

  const handleOrderPlacement = async () => {
    setIsLoading(true);
    await placeOrder({
      variables: {
        cartId: cartInfo?.id || ''
      },
      onError: (error: unknown) => {
        console.log('Order creation error:', error);
        setOrderCreationError(true);
        setIsLoading(false);
      },
      onCompleted: (response: PlaceOrderResponse) => {
        setOrderNumber(response?.placeOrder?.order?.order_number || '');
        setOrderCreationError(false);
        setIsLoading(false);
        onConfirm();
      }
    });
  };

  const HP_TERMS_URL = 'https://www.hp.com/us-en/all-in-plan/terms.html';
  const overviewFinalPath = useModifiedUrl(internalURLs.PrinterOverviewURL);

  const isCissPrinter = selectedPrinterDetails.cissPrinter;

  const selectedPlan = selectedPrinterDetails.inkPlan.find(
    (plan) => plan.recommendedPlan
  );
  // logic to calculate total amount paid in first 24/36 months
  const subtotalCents = Math.round((selectedPlan.totalPrice ?? 0) * 100);
  const prices = cartInfo?.items[0]?.prices;
  const taxCents = Math.round(
    ((prices?.row_total_including_tax?.value ?? 0) -
      (prices?.row_total?.value ?? 0)) *
      100
  );
  const totalCents = subtotalCents + taxCents;
  const months = isCissPrinter ? 36 : 24;
  const totalAmountPaid = formatPrice((totalCents * months) / 100);

  const today = new Date();
  const amoutnDueToday = '$0'; // always would be zero.
  const firstPaymentDate = formatDate(customAddMonths(today, 1), 'MMM dd'); // add 1 month to today
  const cancellationFeeList = selectedPrinterDetails.cancellationFeeList?.map(
    (item: CancellationFeeItem) => item.fees
  );

  const getAfter30DaysCancellationText = (fees: string[]) => {
    const commonText = getTextWithValues(
      'termsOfService.term7.commonText',
      'A cancellation occurring on day 31 through month 12 results in a {{fees1}} early cancellation fee. A cancellation in months 13 through 24 results in a {{fees2}} early cancellation fee.',
      { fees1: fees[1], fees2: fees[2] }
    );

    const cissPrinterText = getTextWithValues(
      'termsOfService.term7.cissPrinterText',
      'A {{fees3}} cancellation fee applies during months 25 through 36.',
      { fees3: fees[3] }
    );

    const footerText = getTextWithValues(
      'termsOfService.term7.footerText',
      'After {{months}} months there is no cancellation fee; you may continue your subscription uninterrupted and are eligible to upgrade to a new All-In Plan printer at any time.',
      { months }
    );

    return isCissPrinter
      ? `${commonText} ${cissPrinterText} ${footerText}`
      : `${commonText} ${footerText}`;
  };

  return (
    <>
      {orderCreationError && (
        <RequestErrorModal
          show={orderCreationError}
          isLoading={isLoading}
          onClose={() => setOrderCreationError(false)}
          onProceed={() => {
            handleOrderPlacement();
          }}
        />
      )}
      <S.TermsServiceContainer>
        <S.TermsContainer>
          <IconDocumentSignature size={32} />
          <S.Heading>
            {getText('termsOfService.title', 'Terms of service')}
          </S.Heading>
          <S.TermsText>
            <S.TermTitle>
              {getText('termsOfService.term1.title', 'Automatic Renewal.')}
            </S.TermTitle>{' '}
            <S.TermDescription>
              {getText(
                'termsOfService.term1.description',
                'Your All-In Plan monthly payments (plus taxes or additional pages) will be automatically charged at the end of each billing cycle and automatically renew and continue unless you cancel. Printer and cartridges remain the property of HP and must be returned to HP upon upgrade, replacement or cancellation.'
              )}
            </S.TermDescription>
          </S.TermsText>
          <S.TermsText>
            <S.TermTitle>
              {getText('termsOfService.term2.title', 'Amount due today')}{' '}
            </S.TermTitle>
            <S.TermDescription>
              {getTextWithValues(
                'termsOfService.term2.title2',
                'is {{amount}}',
                { amount: amoutnDueToday }
              )}
            </S.TermDescription>
          </S.TermsText>
          <S.TermsText>
            <S.TermTitle>
              {getText('termsOfService.term3.title', 'First payment')}{' '}
            </S.TermTitle>
            <S.TermDescription>
              {getTextWithValues(
                'termsOfService.term3.description',
                'is estimated {{firstPaymentDate}}. Once your printer ships, you will be notified of your first payment date via email.',
                {
                  firstPaymentDate
                }
              )}
            </S.TermDescription>
          </S.TermsText>
          <S.TermsText>
            <S.TermTitle>
              {getTextWithValues(
                'termsOfService.term4.title',
                'Total amount paid in first {{months}} months',
                {
                  months
                }
              )}
            </S.TermTitle>{' '}
            <S.TermDescription>
              {getTextWithValues(
                'termsOfService.term4.description',
                'will be {{totalAmountPaid}}, plus any taxes and additional page set fees.',
                {
                  totalAmountPaid
                }
              )}
            </S.TermDescription>
          </S.TermsText>
          <S.TermsText>
            <S.TermTitle>
              {getText(
                'termsOfService.term5.title',
                'Change or cancel plan anytime'
              )}{' '}
            </S.TermTitle>
            <S.TermDescription>
              {getText('termsOfService.term5.description1', 'at')}{' '}
            </S.TermDescription>
            <S.RedirectLink
              href={overviewFinalPath}
              target="_blank"
              rel="noopener noreferrer"
            >
              {getText('termsOfService.term5.description2', 'MyAccount')}
            </S.RedirectLink>{' '}
            <S.TermDescription>
              {getText(
                'termsOfService.term5.description3',
                "or 1-888-447-0148. Individual services can't be cancelled except for paper add on."
              )}
            </S.TermDescription>
          </S.TermsText>
          <S.TermsText>
            <S.TermTitle>
              {getText(
                'termsOfService.term6.title',
                'Cancellation within the first 30 days.'
              )}{' '}
            </S.TermTitle>
            <S.TermDescription>
              {getText(
                'termsOfService.term6.description',
                'Simply send back the used printer and ink supplies with a prepaid shipping label within 10 days to avoid a $130 unreturned printer fee.'
              )}
            </S.TermDescription>
          </S.TermsText>
          <S.TermsText>
            <S.TermTitle>
              {getText(
                'termsOfService.term7.title',
                'Cancellation after the first 30 days:'
              )}{' '}
            </S.TermTitle>
            <S.TermDescription>
              {getAfter30DaysCancellationText(cancellationFeeList)}
            </S.TermDescription>
          </S.TermsText>
          <S.TermsText>
            <S.TermTitle>
              {getText(
                'termsOfService.term8.title',
                'Wi-Fi® or Ethernet connection required.'
              )}{' '}
            </S.TermTitle>
            <S.TermDescription>
              {getText(
                'termsOfService.term8.description',
                'Printer is a smart device which requires an internet connection for setup and to receive ongoing service, including automatic ink delivery. '
              )}
            </S.TermDescription>
          </S.TermsText>
          <S.TermsText>
            <S.TermTitle>
              {getText(
                'termsOfService.term9.title',
                'Other important terms apply.'
              )}{' '}
            </S.TermTitle>
            <S.TermDescription>
              {getText('termsOfService.term9.description1', 'See')}{' '}
            </S.TermDescription>
            <S.RedirectLink
              href={HP_TERMS_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              {getText(
                'termsOfService.term9.description2',
                'HP All-In Plan Terms of Service'
              )}
            </S.RedirectLink>{' '}
            <S.TermDescription>
              {getText(
                'termsOfService.term9.description3',
                'for additional information on early termination, support, replacement service and other important terms.'
              )}
            </S.TermDescription>
          </S.TermsText>
          <S.TermsText>{formatDate(today)}</S.TermsText>
          <S.Separator />
          <S.CheckboxContainer>
            <Checkbox
              id="input-checkbox"
              label={
                <>
                  {getText(
                    'termsOfService.I-agree1',
                    'I agree to the above disclosure and full'
                  )}{' '}
                  <S.RedirectLink
                    href={HP_TERMS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {getText(
                      'termsOfService.I-agree2',
                      'HP All-In Plan Terms of Service.'
                    )}
                  </S.RedirectLink>
                </>
              }
              checked={agreeToTerms}
              onChange={(e) => setAgreeToTerms(e.target.checked)}
            />
          </S.CheckboxContainer>
        </S.TermsContainer>
        <S.UnderstandBlock>
          <S.CheckboxContainer>
            <Checkbox
              id="checkbox"
              label={getText(
                'termsOfService.I-understand',
                'I agree that my current HP All-In Plan subscription will be cancelled when this upgrade plan is created. I also agree to return my current HP All-In Plan printer to HP once I receive the upgrade printer.  HP will provide a free return shipping label for your old printer. When returning it, use the box from your new printer. If you choose to use other packaging, you may incur additional costs from the shipping provider.'
              )}
              checked={understandChecked}
              onChange={(e) => setUnderstandChecked(e.target.checked)}
            />
          </S.CheckboxContainer>
        </S.UnderstandBlock>

        <S.ActionButtons>
          <S.ContinueButton
            disabled={!agreeToTerms || !understandChecked}
            onClick={() => handleOrderPlacement()}
            loading={isLoading}
          >
            Confirm Printer Upgrade
          </S.ContinueButton>
          <Button
            appearance="secondary"
            onClick={() => onBack()}
            data-testid="upgradeReviewBackButton"
          >
            Back
          </Button>
        </S.ActionButtons>
      </S.TermsServiceContainer>
    </>
  );
};

export default TermsofServiceComponent;
